/**
 * LineChart — multi-series time-series chart for Statistics.
 *
 * Wraps uPlot (~30 KB gz) so the rest of Tank doesn't need to know about
 * its imperative API. Redesign-token-aware: phos accent on selected
 * series, muted gray on the unselected, optional amber dashed line for
 * regression markers.
 *
 * Props:
 *   labels:   string[] — x-axis labels (one per bucket)
 *   series:   { key, label, color?, points: (number|null)[] }[]
 *   selected: Set<string> | string[] — keys of series to highlight.
 *             Empty / undefined = show all at full opacity (no filter).
 *             Pass a single-element Set to mimic the prior single-active
 *             behaviour.
 *   onSeriesClick?: (key) => void — fired on legend chip click; caller
 *             owns toggle semantics (add/remove from set).
 *   regressionAt?: { bucket: number, color?: string }  // vertical dashed line
 *   yLabel?:  string — y-axis label, e.g. 'seconds'
 *   height?:  number — pixel height (default 280)
 */

import { useEffect, useMemo, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import styles from './LineChart.module.css';

const DEFAULT_PALETTE = {
  active: '#68ff9a',         // --rd-color-accent
  inactive: 'rgba(255,255,255,0.18)',
  regression: '#ffb35a',     // amber
  axis: 'rgba(255,255,255,0.40)',
  grid: 'rgba(255,255,255,0.06)',
  bg: 'transparent',
};

// Normalise the selected prop to a Set for cheap .has() lookups.
function asSet(selected) {
  if (selected instanceof Set) return selected;
  if (Array.isArray(selected)) return new Set(selected);
  if (typeof selected === 'string' && selected) return new Set([selected]);
  return new Set();
}

export default function LineChart({
  labels,
  series,
  selected,
  onSeriesClick,
  regressionAt,
  yLabel = '',
  height = 280,
  xZoom = null,
}) {
  const containerRef = useRef(null);
  const plotRef = useRef(null);

  const selectedSet = useMemo(() => asSet(selected), [selected]);
  // Empty selection = no filter applied; everything renders at full
  // accent so a fresh page doesn't look like everything is muted.
  const filtering = selectedSet.size > 0;

  // Convert labels (strings) to numeric x values 0..N-1. uPlot wants a
  // numeric x-axis; we only use it to position points. The label per
  // bucket is rendered via a value formatter on the x-axis.
  const xValues = useMemo(() => labels.map((_, i) => i), [labels]);

  const data = useMemo(() => {
    return [xValues, ...series.map((s) => s.points)];
  }, [xValues, series]);

  const opts = useMemo(() => {
    const seriesConfig = [
      { label: 'x' },
      ...series.map((s) => {
        const isActive = !filtering || selectedSet.has(s.key);
        const color = s.color || (isActive ? DEFAULT_PALETTE.active : DEFAULT_PALETTE.inactive);
        return {
          label: s.label || s.key,
          stroke: color,
          width: isActive ? 2 : 1.25,
          spanGaps: true,
          // Always render a marker at every non-null bucket so isolated
          // points are visible (sparse 30d windows with only 1–2 days
          // of data would otherwise render an empty chart).
          points: {
            show: true,
            size: isActive ? 6 : 4,
            stroke: color,
            fill: color,
          },
        };
      }),
    ];
    return {
      width: 720,
      height,
      cursor: {
        y: false,
        drag: { x: false, y: false },
        // Make the canvas itself clickable: nearest series to the click
        // gets toggled via onSeriesClick. Lets users select a line by
        // clicking it directly in the chart, not just via the legend.
        bind: onSeriesClick
          ? {
              click: (u, _target, handler) => (e) => {
                if (e.button !== 0) return handler(e);
                const idx = u.cursor?.idx;
                if (idx != null) {
                  // Find the nearest series by absolute y-distance at idx.
                  let bestKey = null;
                  let bestDist = Infinity;
                  const yPos = u.posToVal(u.cursor.top, 'y');
                  series.forEach((s) => {
                    const v = s.points?.[idx];
                    if (!Number.isFinite(v)) return;
                    const d = Math.abs(v - yPos);
                    if (d < bestDist) {
                      bestDist = d;
                      bestKey = s.key;
                    }
                  });
                  if (bestKey) onSeriesClick(bestKey);
                }
                return handler(e);
              },
            }
          : {},
      },
      legend: { show: false },
      scales: {
        // xZoom narrows the visible bucket range when most buckets are
        // empty — keeps the dots from being lost in whitespace.
        x: xZoom
          ? { time: false, range: () => [xZoom.from, xZoom.to] }
          : { time: false },
        y: { auto: true },
      },
      axes: [
        {
          stroke: DEFAULT_PALETTE.axis,
          grid: { stroke: DEFAULT_PALETTE.grid },
          values: (_u, splits) =>
            splits.map((v) => labels[Math.round(v)] ?? ''),
          font: '10px JetBrains Mono, monospace',
        },
        {
          stroke: DEFAULT_PALETTE.axis,
          grid: { stroke: DEFAULT_PALETTE.grid },
          label: yLabel,
          font: '10px JetBrains Mono, monospace',
        },
      ],
      series: seriesConfig,
      hooks: regressionAt
        ? {
            draw: [
              (u) => {
                const ctx = u.ctx;
                const x = u.valToPos(regressionAt.bucket, 'x', true);
                ctx.save();
                ctx.strokeStyle = regressionAt.color || DEFAULT_PALETTE.regression;
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 4]);
                ctx.beginPath();
                ctx.moveTo(x, u.bbox.top);
                ctx.lineTo(x, u.bbox.top + u.bbox.height);
                ctx.stroke();
                ctx.restore();
              },
            ],
          }
        : {},
    };
  }, [series, selectedSet, filtering, onSeriesClick, labels, regressionAt, yLabel, height, xZoom]);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    // Resize observer for responsive width — uPlot needs explicit setSize.
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 720;
      if (plotRef.current) plotRef.current.setSize({ width: w, height });
    });
    ro.observe(containerRef.current);
    plotRef.current = new uPlot(opts, data, containerRef.current);
    return () => {
      ro.disconnect();
      if (plotRef.current) {
        plotRef.current.destroy();
        plotRef.current = null;
      }
    };
    // Recreate the chart only when structure changes; data + selection
    // updates are handled below via setData / setSeries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series.length, labels.length, height, xZoom?.from, xZoom?.to]);

  // Update data + highlighting without rebuilding the chart.
  useEffect(() => {
    if (!plotRef.current) return;
    plotRef.current.setData(data);
    series.forEach((s, i) => {
      const isActive = !filtering || selectedSet.has(s.key);
      const color = s.color || (isActive ? DEFAULT_PALETTE.active : DEFAULT_PALETTE.inactive);
      plotRef.current.setSeries(i + 1, { stroke: color, width: isActive ? 2 : 1.25 });
    });
  }, [data, series, selectedSet, filtering]);

  return (
    <div className={styles.wrap}>
      <div ref={containerRef} className={styles.canvas} />
      {onSeriesClick && (
        <div className={styles.legend} role="list">
          {series.map((s) => {
            const isActive = !filtering || selectedSet.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                role="listitem"
                className={`${styles.legendItem} ${isActive ? styles.legendActive : ''}`}
                onClick={() => onSeriesClick(s.key)}
              >
                <span
                  className={styles.legendDot}
                  style={{
                    background:
                      s.color || (isActive ? DEFAULT_PALETTE.active : DEFAULT_PALETTE.inactive),
                  }}
                />
                <span>{s.label || s.key}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
